/**
 * Environment Detection Utility
 * Detects which environment the app is running in based on domain/hostname
 * 
 * Environments:
 * - LOCAL: localhost:3000 or 127.0.0.1:3000 → Manus Auth
 * - MANUS_DEV: playxworld.dev or *.manus.space → Manus Auth
 * - FIREBASE_UAT: playxworld.net → Firebase Auth
 * - FIREBASE_PROD: playxworld.com → Firebase Auth (Public Access)
 */

export type Environment = "local" | "manus_dev" | "firebase_uat" | "firebase_prod";
export type AuthType = "manus" | "firebase";

/**
 * Get current environment based on hostname
 */
export function getEnvironment(): Environment {
  if (typeof window === "undefined") return "local";

  const hostname = window.location.hostname;
  const host = window.location.host;

  // Local development
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.startsWith("169.254")) {
    return "local";
  }

  // Manus Dev environments (playxworld.dev, *.manus.space)
  if (hostname.includes("manus.space") || hostname.includes("manus.computer")) {
    return "manus_dev";
  }
  if (hostname.includes("playxworld.dev")) {
    return "manus_dev";
  }

  // Firebase UAT
  if (hostname.includes("playxworld.net") || hostname.includes("playxworld-uat.web.app")) {
    return "firebase_uat";
  }

  // Firebase Production (playxworld.com - public access)
  if (hostname.includes("playxworld.com") || hostname.includes("playxworld-prod.web.app")) {
    return "firebase_prod";
  }

  // Default to local
  return "local";
}

/**
 * Get authentication type for current environment
 */
export function getAuthType(): AuthType {
  const env = getEnvironment();

  // Manus Auth for local and Manus Dev
  if (env === "local" || env === "manus_dev") {
    return "manus";
  }

  // Firebase Auth for Firebase UAT and Production
  if (env === "firebase_uat" || env === "firebase_prod") {
    return "firebase";
  }

  return "manus"; // Default fallback
}

/**
 * Check if using Manus Auth
 */
export function isMausAuth(): boolean {
  return getAuthType() === "manus";
}

/**
 * Check if using Firebase Auth
 */
export function isFirebaseAuth(): boolean {
  return getAuthType() === "firebase";
}

/**
 * Get environment display name
 */
export function getEnvironmentName(): string {
  const env = getEnvironment();
  const names: Record<Environment, string> = {
    local: "Local Development",
    manus_dev: "Manus Dev",
    firebase_uat: "Firebase UAT",
    firebase_prod: "Firebase Production",
  };
  return names[env];
}

/**
 * Get environment config
 */
export interface EnvironmentConfig {
  environment: Environment;
  authType: AuthType;
  environmentName: string;
  hostname: string;
  isDev: boolean;
  isProduction: boolean;
}

export function getEnvironmentConfig(): EnvironmentConfig {
  const hostname = typeof window !== "undefined" ? window.location.hostname : "unknown";

  return {
    environment: getEnvironment(),
    authType: getAuthType(),
    environmentName: getEnvironmentName(),
    hostname,
    isDev: getEnvironment() === "local" || getEnvironment() === "manus_dev",
    isProduction: getEnvironment() === "firebase_prod",
  };
}
